import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import DashboardHeader from './components/DashboardHeader';
import TrialBanner from './components/TrialBanner';
import ErrorBoundary, { SectionErrorBoundary } from './components/ErrorBoundary';
import { SkipLink } from './utils/accessibility';
import ShortcutsCheatsheet from './components/ShortcutsCheatsheet';
import { VoiceCacheProvider, OfflineIndicator } from './components/OfflineVoiceCache';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { Loader2, Settings } from 'lucide-react';
import './App.css';

// Lazy load heavy components for better initial load time
const DemoPage = lazy(() => import('./DemoPage'));
const SignupPage = lazy(() => import('./SignupPage'));
const LoginPage = lazy(() => import('./LoginPage'));
const BrandStyleReport = lazy(() => import('./BrandStyleReport'));
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage'));
const TwoFactorVerificationPage = lazy(() => import('./TwoFactorVerificationPage'));
const PaymentStatusPage = lazy(() => import('./PaymentStatusPage'));
const OAuthCallback = lazy(() => import('./OAuthCallback'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const TeamManagement = lazy(() => import('./TeamManagement'));
const HelpCenter = lazy(() => import('./HelpCenter'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const TrialExpiredPaywall = lazy(() => import('./components/TrialExpiredPaywall'));
const UsageLimitsCard = lazy(() => import('./components/UsageLimitsCard'));
const BusinessSwitcher = lazy(() => import('./components/BusinessSwitcher'));
const Homepage = lazy(() => import('./Homepage'));
import PerformanceMetrics from './components/PerformanceMetrics';

// Lazy load tab components (use default export syntax)
const BusinessTab = lazy(() => import('./components/tabs/BusinessTab'));
const ContentTab = lazy(() => import('./components/tabs/ContentTab'));
const SocialTab = lazy(() => import('./components/tabs/SocialTab'));
const ScheduleTab = lazy(() => import('./components/tabs/ScheduleTab'));
const AnalyticsTab = lazy(() => import('./components/tabs/AnalyticsTab'));
const SettingsTab = lazy(() => import('./components/tabs/SettingsTab'));

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
      <p className="text-gray-500 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Tab loading spinner (smaller)
const TabLoader = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Component
const Dashboard = () => {
  const { user, token, logout } = useAuth(); // Get authenticated user, token, and logout function from context
  const navigate = useNavigate();
  const location = useLocation();
  
  // Trial status state
  const [trialExpired, setTrialExpired] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  
  // Shared dashboard state
  const [userBusiness, setUserBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [generatedContent, setGeneratedContent] = useState([]);
  const [scheduledContent, setScheduledContent] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);

  // Check trial status on mount and when user changes
  useEffect(() => {
    if (user?.trialInfo) {
      setTrialInfo(user.trialInfo);
      if (user.trialInfo.requiresPayment || user.subscriptionType === 'expired') {
        setTrialExpired(true);
      }
    }
  }, [user]);

  // Track if we've loaded data for current user to prevent double fetches
  const [dataLoadedForUser, setDataLoadedForUser] = useState(null);

  useEffect(() => {
    // Only load data once per user session, not on every route change
    if (user?.id && token && dataLoadedForUser !== user.id) {
      console.log('Loading dashboard data for user:', user.id);
      loadDashboardData();
      setDataLoadedForUser(user.id);
    }
  }, [user?.id, token, dataLoadedForUser]);

  const loadDashboardData = async () => {
    if (!user?.id || !token) {
      console.log('loadDashboardData aborted - user.id:', user?.id, 'token:', !!token);
      return;
    }
    
    try {
      setBusinessLoading(true);
      console.log('Starting data load for user:', user.id);
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load connected social media accounts
      try {
        console.log('Loading connected social accounts...');
        const accountsResponse = await axios.get(`${API}/social-accounts`, { headers });
        console.log('Connected accounts loaded:', accountsResponse.data.length);
        setConnectedAccounts(accountsResponse.data);
      } catch (error) {
        console.log('No connected accounts or error:', error.message);
        setConnectedAccounts([]);
      }
      
      // Check if user has a business profile
      console.log('Checking if user has business...');
      const businessExistsResponse = await axios.get(`${API}/users/${user.id}/business-exists`, { headers });
      console.log('Business exists response:', businessExistsResponse.data);
      
      let businessToLoad = null;
      
      if (businessExistsResponse.data.has_business) {
        // Load user's owned business profile
        console.log('Loading owned business profile...');
        const businessResponse = await axios.get(`${API}/users/${user.id}/business`, { headers });
        console.log('Business loaded:', businessResponse.data.name);
        businessToLoad = businessResponse.data;
      } else {
        // User doesn't own a business - check if they have team memberships
        console.log('User has no owned business, checking team memberships...');
        try {
          const teamsResponse = await axios.get(`${API}/businesses/my-teams`, { headers });
          console.log('Teams response:', teamsResponse.data);
          
          if (teamsResponse.data.teams && teamsResponse.data.teams.length > 0) {
            // User is a member of at least one team - load the first one
            const firstTeam = teamsResponse.data.teams[0];
            console.log('Loading team business:', firstTeam.name, 'with role:', firstTeam.role);
            
            const teamBusinessResponse = await axios.get(`${API}/businesses/${firstTeam.id}`, { headers });
            businessToLoad = teamBusinessResponse.data;
            toast.info(`Viewing ${firstTeam.name} as ${firstTeam.role}`);
          } else {
            console.log('User has no team memberships either');
          }
        } catch (error) {
          console.log('Error checking team memberships:', error.message);
        }
      }
      
      if (businessToLoad) {
        setUserBusiness(businessToLoad);
        
        // Load generated content (paginated - first 10 items)
        console.log('Loading generated content...');
        const contentResponse = await axios.get(`${API}/businesses/${businessToLoad.id}/content`, { 
          headers,
          params: { limit: 10, offset: 0 }
        });
        console.log('Generated content loaded:', contentResponse.data.length, 'items');
        setGeneratedContent(contentResponse.data);
        
        // Load scheduled content
        try {
          console.log('Loading scheduled content...');
          const scheduledResponse = await axios.get(`${API}/content/scheduled?business_id=${businessToLoad.id}`, { headers });
          console.log('Scheduled content loaded:', scheduledResponse.data.length, 'items');
          setScheduledContent(scheduledResponse.data);
        } catch (error) {
          console.log('No scheduled content available:', error.message);
          setScheduledContent([]);
        }
        
        // Load analytics
        try {
          console.log('Loading analytics...');
          const analyticsResponse = await axios.get(`${API}/businesses/${businessToLoad.id}/analytics`, { headers });
          console.log('Analytics loaded');
          setAnalytics(analyticsResponse.data);
        } catch (error) {
          console.log('No analytics available:', error.message);
          setAnalytics(null);
        }
        
        console.log('Dashboard data load complete!');
      } else {
        console.log('User has no business profile and no team memberships');
        setUserBusiness(null);
        setGeneratedContent([]);
        setScheduledContent([]);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleBusinessUpdate = (updatedBusiness) => {
    setUserBusiness(updatedBusiness);
    // Reload dashboard data when business is updated
    loadDashboardData();
  };

  const handleBusinessSwitch = async (newBusiness, role) => {
    setBusinessLoading(true);
    try {
      setUserBusiness(newBusiness);
      
      // Load content for the new business (paginated - first 10 items)
      const headers = { Authorization: `Bearer ${token}` };
      const contentResponse = await axios.get(`${API}/businesses/${newBusiness.id}/content`, { 
        headers,
        params: { limit: 10, offset: 0 }
      });
      setGeneratedContent(contentResponse.data);
      
      // Load scheduled content for the new business
      try {
        const scheduledResponse = await axios.get(`${API}/content/scheduled?business_id=${newBusiness.id}`, { headers });
        setScheduledContent(scheduledResponse.data);
      } catch (error) {
        setScheduledContent([]);
      }
      
      toast.success(`Switched to ${newBusiness.name}`);
    } catch (error) {
      console.error('Failed to switch business:', error);
      toast.error('Failed to switch business');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleContentUpdate = (newContent) => {
    if (Array.isArray(newContent)) {
      setGeneratedContent([...newContent, ...generatedContent]);
    } else if (newContent && newContent.id) {
      // If an item with this id already exists, replace it in place (regenerate flow).
      // Otherwise prepend as a new item.
      setGeneratedContent(prev => {
        const idx = prev.findIndex(c => c.id === newContent.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = newContent;
          return copy;
        }
        return [newContent, ...prev];
      });
    } else {
      setGeneratedContent([newContent, ...generatedContent]);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Show paywall if trial has expired
  if (trialExpired) {
    return (
      <TrialExpiredPaywall 
        user={user} 
        trialInfo={trialInfo}
        onSubscriptionComplete={() => {
          setTrialExpired(false);
          window.location.reload();
        }}
      />
    );
  }

  // Simple return for the dashboard - BusinessManager handles all business logic
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950">
      {/* Navigation */}
      <DashboardHeader user={user} onLogout={handleLogout} />

      {/* Trial Status Banner */}
      <TrialBanner trialInfo={trialInfo} />

      <main id="main-content" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8" role="main" aria-label="Dashboard content">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">Social Media Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Generate and manage AI-powered content</p>
            </div>
            <BusinessSwitcher 
              currentBusiness={userBusiness}
              onBusinessChange={handleBusinessSwitch}
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="business" className="space-y-4 sm:space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-gray-100/80 rounded-lg" aria-label="Dashboard sections">
            <TabsTrigger value="business" data-testid="main-business-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" aria-label="Business profile">Business</TabsTrigger>
            <TabsTrigger value="content" data-testid="main-content-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" aria-label="Content generation">Content</TabsTrigger>
            <TabsTrigger value="social" data-testid="main-social-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" aria-label="Social media connections">Social</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="main-schedule-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" aria-label="Content calendar">Calendar</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="main-analytics-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" aria-label="Analytics and insights">Analytics</TabsTrigger>
            <TabsTrigger value="settings" data-testid="main-settings-tab" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1" aria-label="Account settings">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
              <span>Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="business" className="space-y-6" role="tabpanel" aria-label="Business profile section">
            <Suspense fallback={<TabLoader />}>
              <BusinessTab 
                user={user}
                userBusiness={userBusiness}
                onBusinessUpdate={handleBusinessUpdate}
                loading={businessLoading}
              />
            </Suspense>
          </TabsContent>

          <TabsContent value="content" className="space-y-6" role="tabpanel" aria-label="Content generation section">
            <Suspense fallback={<TabLoader />}>
              <ContentTab 
                user={user}
                userBusiness={userBusiness}
                generatedContent={generatedContent}
                onContentUpdate={handleContentUpdate}
                loading={businessLoading}
              />
            </Suspense>
          </TabsContent>

          <TabsContent value="social" role="tabpanel" aria-label="Social media connections section">
            <Suspense fallback={<TabLoader />}>
              <SocialTab onAccountsChange={setConnectedAccounts} />
            </Suspense>
          </TabsContent>

          <TabsContent value="schedule" role="tabpanel" aria-label="Content calendar section">
            <Suspense fallback={<TabLoader />}>
              <ScheduleTab 
                userBusiness={userBusiness}
                scheduledContent={scheduledContent}
                generatedContent={generatedContent}
                loading={businessLoading}
              />
            </Suspense>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6" role="tabpanel" aria-label="Analytics and insights section">
            <Suspense fallback={<TabLoader />}>
              <AnalyticsTab 
                userBusiness={userBusiness}
                analytics={analytics}
                generatedContent={generatedContent}
                loading={businessLoading}
                connectedAccounts={connectedAccounts}
              />
            </Suspense>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4 sm:space-y-6" role="tabpanel" aria-label="Account settings section">
            <Suspense fallback={<TabLoader />}>
              <SettingsTab 
                user={user}
                token={token}
                userBusiness={userBusiness}
              />
            </Suspense>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Team Page Wrapper - gets businessId from user context
const TeamPageWrapper = () => {
  const { user } = useAuth();
  const [businessId, setBusinessId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await axios.get(`${API}/users/${user?.id}/business`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBusinessId(res.data.id);
      } catch (e) {
        console.error('No business found');
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchBusiness();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Business Found</h2>
          <p className="text-gray-500 mb-4">Create a business profile first to manage your team.</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <TeamManagement businessId={businessId} onClose={() => navigate('/dashboard')} />
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <ErrorBoundary section="Application">
      <ThemeProvider>
      <VoiceCacheProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <SkipLink href="#main-content" />
            <div className="App" role="application" aria-label="Viralla AI Social Media Automation">
              <SessionTimeoutModal />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Homepage />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/verify-2fa" element={<TwoFactorVerificationPage />} />
                  <Route path="/payment-status" element={<PaymentStatusPage />} />
                  <Route path="/payment/success" element={<PaymentStatusPage />} />
                  <Route path="/payment/cancel" element={<PaymentStatusPage />} />
                  <Route path="/auth/oauth/:platform/callback" element={<OAuthCallback />} />
                  <Route path="/help" element={<HelpCenter />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/brand-style-report" element={
                    <ProtectedRoute>
                      <SectionErrorBoundary section="Brand Style Report">
                        <BrandStyleReport />
                      </SectionErrorBoundary>
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <SectionErrorBoundary section="Profile">
                        <ProfilePage />
                      </SectionErrorBoundary>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <SectionErrorBoundary section="Dashboard">
                        <Dashboard />
                      </SectionErrorBoundary>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <SectionErrorBoundary section="Admin Dashboard">
                        <AdminDashboard />
                      </SectionErrorBoundary>
                    </ProtectedRoute>
                  } />
                  <Route path="/team" element={
                    <ProtectedRoute>
                      <SectionErrorBoundary section="Team Management">
                        <TeamPageWrapper />
                      </SectionErrorBoundary>
                    </ProtectedRoute>
                  } />
                </Routes>
              </Suspense>
              {/* Performance Metrics Widget - Click the Activity icon in bottom right to show */}
              <PerformanceMetrics />
              {/* Keyboard Shortcuts Cheatsheet - Press Ctrl+/ to toggle */}
              <ShortcutsCheatsheet position="bottom-right" />
              {/* Offline indicator for voice recordings */}
              <div className="fixed bottom-4 left-4 z-40">
                <OfflineIndicator />
              </div>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </VoiceCacheProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;